import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'

interface MarkdownRendererProps {
  content: string
  className?: string
}

/**
 * MarkdownRenderer - Component to render markdown content with syntax highlighting
 * Supports GitHub Flavored Markdown (GFM) features like tables, strikethrough, etc.
 */
export default function MarkdownRenderer({ 
  content, 
  className
}: MarkdownRendererProps) {
  return (
    <div className={cn(
      'markdown-content',
      className
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Customize code blocks
          code: ({ className: codeClassName, children, ...props }: any) => {
            const isInline = !codeClassName?.includes('language-');
            if (isInline) {
              return (
                <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono" {...props}>
                  {children}
                </code>
              )
            }
            return (
              <code className="block p-3 bg-muted rounded-md text-sm font-mono overflow-x-auto" {...props}>
                {children}
              </code>
            )
          },
          // Customize pre blocks
          pre: ({ children }) => {
            return <div className="my-2">{children}</div>
          },
          // Customize paragraphs
          p: ({ children }) => {
            return <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>
          },
          // Customize headings
          h1: ({ children }) => <h1 className="text-2xl font-bold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-semibold mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold mt-2 mb-1">{children}</h3>,
          // Customize lists
          ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="ml-2">{children}</li>,
          // Customize blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-2">
              {children}
            </blockquote>
          ),
          // Customize links
          a: ({ href, children }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {children}
            </a>
          ),
          // Customize tables
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full border-collapse border border-border">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-border">{children}</tr>,
          th: ({ children }) => (
            <th className="px-4 py-2 text-left font-semibold text-sm border border-border">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm border border-border">
              {children}
            </td>
          ),
          // Customize horizontal rules
          hr: () => <hr className="my-4 border-t border-border" />,
          // Customize strong/bold
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          // Customize emphasis/italic
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
